'use client';

import { useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useLocale } from 'next-intl';
import { ar } from '@daypicker/react/locale';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { BaseFormFieldProps, DatePickerConfig } from '@/types/base-form';

interface FormDatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends BaseFormFieldProps<TFieldValues, TName> {
  config?: DatePickerConfig;
}

function FormDatePicker<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  description,
  required,
  config = {},
  disabled,
  className
}: FormDatePickerProps<TFieldValues, TName>) {
  const {
    minDate,
    maxDate,
    disabledDates = [],
    placeholder = 'Pick a date',
    captionLayout,
    startMonth,
    endMonth,
    outputFormat = 'string'
  } = config;

  const currentLocale = useLocale();
  const dayPickerLocale = currentLocale === 'ar' ? ar : undefined;
  const calendarDir = currentLocale === 'ar' ? 'rtl' : 'ltr';

  const [month, setMonth] = useState<Date | undefined>(undefined);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('flex flex-col', className)}>
          {label && (
            <FormLabel>
              {label}
              {required && <span className='ms-1 text-red-500'>*</span>}
            </FormLabel>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant='outline'
                  className={cn(
                    'w-full ps-3 text-start font-normal',
                    !field.value && 'text-muted-foreground'
                  )}
                  disabled={disabled}
                >
                  {field.value ? (
                    format(new Date(field.value), 'PPP')
                  ) : (
                    <span>{placeholder}</span>
                  )}
                  <CalendarIcon className='ms-auto h-4 w-4 opacity-50' />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
              <Calendar
                mode='single'
                locale={dayPickerLocale}
                dir={calendarDir}
                selected={field.value ? new Date(field.value) : undefined}
                onSelect={(date) => {
                  if (!date) {
                    field.onChange('');
                    return;
                  }
                  field.onChange(
                    outputFormat === 'date'
                      ? date
                      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                  );
                }}
                disabled={(date) => {
                  if (minDate && date < minDate) return true;
                  if (maxDate && date > maxDate) return true;
                  return disabledDates.some(
                    (disabledDate) => date.getTime() === disabledDate.getTime()
                  );
                }}
                captionLayout={captionLayout}
                startMonth={startMonth}
                endMonth={endMonth}
                month={
                  month ?? (field.value ? new Date(field.value) : undefined)
                }
                onMonthChange={setMonth}
                autoFocus
              />
            </PopoverContent>
          </Popover>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export { FormDatePicker };
